import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Button, Checkbox, Dialog, Price, Select } from '@artosera/ui';
import { Link } from 'react-router';
import {
  useEnvoyerRelancesPaiement,
  usePreparerRelancesPaiement,
  useRelancesPaiement,
  type RelancePaiement,
} from '../../api/paiementEnLigne.js';
import { ventesApi } from '../../api/ventes.js';
import { appelDeRequete, cleMetier } from '../../api/cles.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import {
  ErreurPaiement,
  HistoriqueRelances,
  PagesPaiement,
  useTextePaiement,
} from './paiementUi.js';
import styles from './PaiementEnLigne.module.css';

export function Relances() {
  const { galerie, galerieSlug } = useShellContext();
  const { t, label, langue } = useTextePaiement();
  const [page, setPage] = useState(1);
  const [etat, setEtat] = useState<RelancePaiement['etat'] | 'tous'>('a_envoyer');
  const [selection, setSelection] = useState<string[]>([]);
  const [confirmation, setConfirmation] = useState(false);
  const [resultats, setResultats] = useState<RelancePaiement[]>([]);
  const lecture = useRelancesPaiement(galerieSlug, {
    page,
    parPage: 100,
    ...(etat !== 'tous' ? { etat } : {}),
  });
  const envoyer = useEnvoyerRelancesPaiement(galerieSlug);
  const preparer = usePreparerRelancesPaiement(galerieSlug);
  const peutEcrire = galerieALaction(galerie, 'online-payments:write');
  const choisies = (lecture.data?.relances ?? []).filter(
    (r) => selection.includes(r._id) && (r.etat === 'a_envoyer' || r.etat === 'a_verifier'),
  );
  const factureIds = [...new Set(choisies.map((r) => r.factureId))];
  const factures = useQueries({
    queries: factureIds.map((id) => ({
      queryKey: cleMetier(galerieSlug, 'paiement-en-ligne', 'client-facture', id),
      queryFn: (c: { signal: AbortSignal; queryKey: readonly unknown[] }) =>
        ventesApi.obtenir(galerieSlug, id, appelDeRequete(c)),
      enabled: confirmation,
    })),
  });
  const clients = new Set(factures.flatMap((f) => (f.data ? [f.data.facture.contactId] : []))).size;
  const clientsCharges =
    factures.length > 0 && factures.every((f) => f.isSuccess && !!f.data.facture.contactId);
  return (
    <section className={styles.section}>
      <h2>{label('relances')}</h2>
      <p>{label('captureExplication')}</p>
      {peutEcrire && (
        <Button loading={preparer.isPending} onClick={() => preparer.mutate()}>
          {label('preparer')}
        </Button>
      )}
      <p>{label('preparationAutomatique')}</p>
      {preparer.data && (
        <p role="status">
          {t('regie:paiementEnLigne.preparees', { count: preparer.data.preparees })}
        </p>
      )}
      <ErreurPaiement erreur={preparer.error} />
      <ErreurPaiement erreur={lecture.error} />
      <Field label={label('etat')} htmlFor="relances-etat">
        <Select
          id="relances-etat"
          value={etat}
          onValueChange={(v) => {
            setEtat(v as typeof etat);
            setPage(1);
            setSelection([]);
          }}
          options={[
            'a_envoyer',
            'tous',
            'en_cours',
            'a_verifier',
            'envoyee',
            'capture',
            'echec',
            'sans_objet',
          ].map((v) => ({ value: v, label: label(`etats.${v}`) }))}
        />
      </Field>
      {lecture.isLoading && <p>{t('commun:chargement')}</p>}
      {lecture.data && (
        <>
          <div className={styles.tableauEnveloppe}>
            <table className={styles.tableau}>
              <thead>
                <tr>
                  {['selection', 'facture', 'palier', 'resteDu', 'etat'].map((v) => (
                    <th scope="col" key={v}>
                      {label(v)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lecture.data.relances.map((r) => (
                  <tr key={r._id}>
                    <td>
                      {peutEcrire && (r.etat === 'a_envoyer' || r.etat === 'a_verifier') && (
                        <Checkbox
                          label={`${label('selectionner')} ${r.factureId}`}
                          checked={selection.includes(r._id)}
                          onCheckedChange={(checked) =>
                            setSelection(
                              checked === true
                                ? [...selection, r._id]
                                : selection.filter((id) => id !== r._id),
                            )
                          }
                        />
                      )}
                    </td>
                    <td>
                      <Link to={`/${galerieSlug}/ventes/${r.factureId}`}>{r.factureId}</Link>
                      {r.destinataire && <> · {r.destinataire}</>}
                    </td>
                    <td>
                      {r.rang} · {label(`tons.${r.ton}`)}
                    </td>
                    <td>
                      <Price cents={r.resteDuCentimes} lang={langue} />
                    </td>
                    <td>{label(`etats.${r.etat}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PagesPaiement
            pagination={lecture.data.pagination}
            changer={(p) => {
              setPage(p);
              setSelection([]);
            }}
          />
        </>
      )}
      {peutEcrire && (
        <Button
          disabled={!choisies.length}
          onClick={() => {
            envoyer.reset();
            setConfirmation(true);
          }}
        >
          {label('envoyerLot')}
        </Button>
      )}
      {resultats.length > 0 && (
        <section aria-live="polite">
          <h3>{label('resultats')}</h3>
          <HistoriqueRelances galerieSlug={galerieSlug} relances={resultats} />
        </section>
      )}
      <Dialog
        open={confirmation}
        onOpenChange={setConfirmation}
        title={label('confirmerLot')}
        closeLabel={t('commun:fermer')}
      >
        <p>
          {clientsCharges
            ? t('regie:paiementEnLigne.clientsContactes', { count: clients })
            : t('commun:chargement')}
        </p>
        <p>{label('contactsRelus')}</p>
        {choisies.some((r) => r.etat === 'a_verifier') && <p>{label('verifierAvantRenvoi')}</p>}
        {factures.map((f, i) => (
          <ErreurPaiement key={factureIds[i]} erreur={f.error} />
        ))}
        <ErreurPaiement erreur={envoyer.error} />
        <Button
          disabled={!clientsCharges || !choisies.length}
          loading={envoyer.isPending}
          onClick={() => {
            void envoyer
              .mutateAsync(choisies.map((r) => r._id))
              .then((r) => {
                setResultats(r.relances.filter((v): v is RelancePaiement => v !== null));
                setSelection([]);
                setConfirmation(false);
              })
              .catch(() => {});
          }}
        >
          {label('confirmer')}
        </Button>
      </Dialog>
    </section>
  );
}
